import React, { useState } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    TextField,
    FormHelperText,
    Button,
    Divider,
} from '@mui/material';
import { useTranslate, useInput } from 'react-admin';
import { Translate, ContentCopy, SwapHoriz } from '@mui/icons-material';
import * as OpenCC from 'opencc-js';

interface BannerMultiLanguageFieldsProps {
    formData?: any;
}

export const BannerMultiLanguageFields: React.FC<BannerMultiLanguageFieldsProps> = ({
    formData
}) => {
    const [currentLang, setCurrentLang] = useState(0);
    const translate = useTranslate();
    const translationsInput = useInput({ source: 'translations' });

    const languages = [
        { code: 'en', name: 'English' },
        { code: 'zh-TW', name: '繁體中文' },
        { code: 'zh-CN', name: '简体中文' }
    ];

    const fields = [
        {
            name: 'title',
            label: translate('resources.banners.fields.title'),
            required: true,
            helperText: translate('resources.banners.multilang.titleHelperText'),
            multiline: false
        },
        {
            name: 'subtitle',
            label: translate('resources.banners.fields.subtitle'),
            required: false,
            helperText: translate('resources.banners.multilang.subtitleHelperText'),
            multiline: false
        },
        {
            name: 'actionLabel',
            label: translate('resources.banners.fields.actionLabel'),
            required: false,
            helperText: translate('resources.banners.multilang.actionLabelHelperText'),
            multiline: false
        },
        {
            name: 'promotionContent',
            label: translate('resources.banners.fields.promotionContent'),
            required: false,
            helperText: translate('resources.banners.multilang.promotionContentHelperText'),
            multiline: true,
            rows: 6
        },
        {
            name: 'modalButtonText',
            label: translate('resources.banners.fields.modalButtonText'),
            required: false,
            helperText: translate('resources.banners.multilang.modalButtonTextHelperText'),
            multiline: false
        }
    ];

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentLang(newValue);
    };

    // Get translations from form data
    const translations = React.useMemo(() =>
        translationsInput.field.value || [],
        [translationsInput.field.value]
    );

    const currentTranslation = React.useMemo(() =>
        translations[currentLang] || {},
        [translations, currentLang]
    );

    // Initialize translations array if empty
    React.useEffect(() => {
        console.log('[BannerMultiLanguageFields] Current translations:', translations);
        console.log('[BannerMultiLanguageFields] FormData:', formData);

        if (translations.length === 0) {
            const initialTranslations = languages.map(lang => ({
                language: lang.code,
                title: '',
                subtitle: '',
                actionLabel: '',
                promotionContent: ''
            }));
            console.log('[BannerMultiLanguageFields] Initializing with:', initialTranslations);
            translationsInput.field.onChange(initialTranslations);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const handleFieldChange = (fieldName: string, value: string) => {
        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }
        updatedTranslations[currentLang][fieldName] = value;
        translationsInput.field.onChange(updatedTranslations);
    };

    const handleCopyFromBasic = () => {
        if (!formData) return;

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Copy values from basic information for each field
        fields.forEach(field => {
            if (formData[field.name] !== undefined) {
                updatedTranslations[currentLang][field.name] = formData[field.name] || '';
            }
        });

        // Update translations in a single call
        translationsInput.field.onChange(updatedTranslations);
    };

    const handleTranslateFromSimplified = () => {
        // Find the simplified Chinese translation (zh-CN)
        const simplifiedIndex = languages.findIndex(lang => lang.code === 'zh-CN');
        const simplifiedTranslation = translations[simplifiedIndex];

        if (!simplifiedTranslation) {
            return;
        }

        // Create converter from Simplified to Traditional
        const converter = OpenCC.Converter({ from: 'cn', to: 'tw' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from simplified to traditional
        fields.forEach(field => {
            const simplifiedValue = simplifiedTranslation[field.name];
            if (simplifiedValue && typeof simplifiedValue === 'string') {
                updatedTranslations[currentLang][field.name] = converter(simplifiedValue);
            }
        });

        translationsInput.field.onChange(updatedTranslations);
    };

    const handleTranslateFromTraditional = () => {
        // Find the traditional Chinese translation (zh-TW)
        const traditionalIndex = languages.findIndex(lang => lang.code === 'zh-TW');
        const traditionalTranslation = translations[traditionalIndex];

        if (!traditionalTranslation) {
            return;
        }

        // Create converter from Traditional to Simplified
        const converter = OpenCC.Converter({ from: 'tw', to: 'cn' });

        const updatedTranslations = [...translations];
        if (!updatedTranslations[currentLang]) {
            updatedTranslations[currentLang] = { language: languages[currentLang].code };
        }

        // Convert each text field from traditional to simplified
        fields.forEach(field => {
            const traditionalValue = traditionalTranslation[field.name];
            if (traditionalValue && typeof traditionalValue === 'string') {
                updatedTranslations[currentLang][field.name] = converter(traditionalValue);
            }
        });

        translationsInput.field.onChange(updatedTranslations);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Translate sx={{ mr: 1, color: '#00C853' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {translate('resources.banners.multilang.title')}
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('resources.banners.multilang.helperText')}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 2 }}>
                <Tabs
                    value={currentLang}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            minWidth: { xs: 0, sm: 90 },
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            textTransform: 'none',
                            fontWeight: 500,
                        }
                    }}
                >
                    {languages.map((lang) => (
                        <Tab key={lang.code} label={lang.name} />
                    ))}
                </Tabs>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyFromBasic}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#00C853',
                            color: '#00C853',
                            '&:hover': {
                                borderColor: '#00A843',
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            }
                        }}
                    >
                        {translate('resources.banners.multilang.copyFromBasic')}
                    </Button>

                    {/* Show translation button for Traditional Chinese tab */}
                    {languages[currentLang].code === 'zh-TW' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromSimplified}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            {translate('resources.banners.multilang.translateFromSimplified')}
                        </Button>
                    )}

                    {/* Show translation button for Simplified Chinese tab */}
                    {languages[currentLang].code === 'zh-CN' && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<SwapHoriz />}
                            onClick={handleTranslateFromTraditional}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                    borderColor: '#1976D2',
                                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                }
                            }}
                        >
                            {translate('resources.banners.multilang.translateFromTraditional')}
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Banner Content Section */}
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                        {translate('resources.banners.sections.bannerContent')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {fields.filter(field => {
                            // Always show title
                            if (field.name === 'title') return true;

                            // Show subtitle only for gradient banners (image banners don't show subtitle)
                            if (field.name === 'subtitle') {
                                return formData?.displayType === 'gradient';
                            }

                            // Show actionLabel only if action exists and is NOT promotion_modal
                            if (field.name === 'actionLabel') {
                                return formData?.actionType &&
                                       formData.actionType !== 'none' &&
                                       formData.actionType !== 'promotion_modal';
                            }

                            // Don't show modal fields here
                            return false;
                        }).map((field) => (
                            <Box key={field.name}>
                                <TextField
                                    fullWidth
                                    label={field.label}
                                    value={currentTranslation[field.name] || ''}
                                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                    multiline={field.multiline}
                                    rows={field.rows}
                                    required={field.required && languages[currentLang].code === 'en'}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#FAFAFA',
                                            '&:hover': {
                                                backgroundColor: '#F5F5F5',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#FFFFFF',
                                            },
                                        },
                                    }}
                                />
                                {field.helperText && (
                                    <FormHelperText>{field.helperText}</FormHelperText>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Modal Content Section - Only show for promotion_modal */}
                {formData?.actionType === 'promotion_modal' && (
                    <>
                        <Divider />
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                                {translate('resources.banners.sections.modalContent')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {fields.filter(field => {
                                    // Show only modal-related fields
                                    return field.name === 'promotionContent' || field.name === 'modalButtonText';
                                }).map((field) => (
                                    <Box key={field.name}>
                                        <TextField
                                            fullWidth
                                            label={field.label}
                                            value={currentTranslation[field.name] || ''}
                                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                            multiline={field.multiline}
                                            rows={field.rows}
                                            required={field.required && languages[currentLang].code === 'en'}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#FAFAFA',
                                                    '&:hover': {
                                                        backgroundColor: '#F5F5F5',
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: '#FFFFFF',
                                                    },
                                                },
                                            }}
                                        />
                                        {field.helperText && (
                                            <FormHelperText>{field.helperText}</FormHelperText>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            {languages[currentLang].code !== 'en' && (
                <Box sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#E8F5E9',
                    borderRadius: 1,
                    border: '1px solid #C8E6C9'
                }}>
                    <Typography variant="caption" color="text.secondary">
                        {translate('resources.banners.multilang.nonEnglishNote')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};